// LogsTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { formatDistanceToNow } from 'date-fns';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Activity, Bot, Cpu, Sparkles, Brain, Eye } from 'lucide-react';

export const LogsTab = ({
  executionLogs = [],
  onLogClick
}) => {
  return (
    <TabsContent value="logs" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Execution Logs
          </CardTitle>
          <CardDescription>Click any log to view detailed AI analysis with Left Brain / Right Brain / Cortex outputs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {executionLogs.map(log => (
              <div 
                key={log.id} 
                className="p-4 border rounded-lg hover:shadow-md cursor-pointer transition-all hover:bg-slate-50"
                onClick={() => onLogClick(log)}
                data-testid={`log-item-${log.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600">
                      <AvatarFallback className="bg-transparent text-white font-bold">
                        <Bot className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="font-medium">{log.agent_name}</span>
                      <span className="text-sm text-muted-foreground ml-2">{log.action}</span>
                      <Eye className="h-3 w-3 inline ml-2 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={log.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {log.success ? 'Success' : 'Failed'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{log.duration_ms}ms</span>
                    <span className="text-xs text-muted-foreground">
                      {log.timestamp ? formatDistanceToNow(new Date(log.timestamp), { addSuffix: true }) : ''}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div className="bg-blue-50 p-2 rounded border border-blue-100">
                    <p className="font-medium text-blue-700 mb-1 flex items-center gap-1">
                      <Cpu className="h-3 w-3" /> Left Brain
                    </p>
                    <p className="text-blue-600 line-clamp-2">{log.left_brain_output?.substring(0, 150) || 'No output'}...</p>
                  </div>
                  <div className="bg-purple-50 p-2 rounded border border-purple-100">
                    <p className="font-medium text-purple-700 mb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Right Brain
                    </p>
                    <p className="text-purple-600 line-clamp-2">{log.right_brain_output?.substring(0, 150) || 'No output'}...</p>
                  </div>
                  <div className="bg-emerald-50 p-2 rounded border border-emerald-100">
                    <p className="font-medium text-emerald-700 mb-1 flex items-center gap-1">
                      <Brain className="h-3 w-3" /> Cortex
                    </p>
                    <p className="text-emerald-600 line-clamp-2">{log.brain_stem_output?.substring(0, 150) || 'No output'}...</p>
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">Click to view full analysis</p>
              </div>
            ))}
            {executionLogs.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No execution logs yet</p>
                <p className="text-xs text-muted-foreground mt-1">Run a demo or assign tasks to AI agents to generate logs</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default LogsTab;
